import React, { useState, useRef, useEffect } from "react";
import { Send, Brain, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = '/api/vision/chat';
const MAX_LENGTH = 2000;
const SESSION_KEY = 'vision_token';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Welcome! I'm VISION, your AI knowledge assistant. Ask me about DMI patterns, learnings, troubleshooting, or any project documentation. I have access to 81+ learnings and skills from the DMI ecosystem.",
  timestamp: new Date().toISOString()
};

export const VisionChat = () => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > MAX_LENGTH || isLoading) return;

    const userMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      // Get session token for authentication
      const visionToken = sessionStorage.getItem(SESSION_KEY);
      
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Vision-Token': visionToken || ''
        },
        body: JSON.stringify({
          content: trimmed,
          messages: messages.slice(-10).map(m => ({ role: m.role, content: m.content }))
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        // Handle session expired - redirect to re-authenticate
        if (errData.code === 'VISION_TOKEN_EXPIRED' || errData.code === 'VISION_AUTH_REQUIRED') {
          sessionStorage.removeItem(SESSION_KEY);
          window.location.reload(); // Force re-auth via PasswordGate
          return;
        }
        throw new Error(errData.error || 'Failed to get response');
      }

      const data = await response.json();
      
      // Handle Pinecone response format
      const assistantContent = data.message?.content || data.content || 'No response received';
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date().toISOString(),
        citations: data.citations || []
      }]);

    } catch (err) {
      console.error('VISION chat error:', err);
      setError(err.message);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: '32px 16px',
      backgroundColor: 'hsl(220 14% 96%)'
    }}>
      <div style={{
        maxWidth: '700px',
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <p style={{ 
            fontSize: '14px', 
            color: 'hsl(220 9% 46%)',
            marginBottom: '8px'
          }}>
            ⚡ Jarvis Sovereign System
          </p>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '8px' 
          }}>
            <Brain style={{ 
              width: '36px', 
              height: '36px', 
              color: 'hsl(221 83% 53%)' 
            }} />
            <h1 style={{ 
              fontSize: '32px', 
              fontWeight: '700',
              color: 'hsl(220 15% 15%)',
              margin: 0
            }}>
              VISION
            </h1>
          </div>
          <p style={{ 
            color: 'hsl(220 9% 46%)', 
            marginTop: '4px' 
          }}>
            Your AI Knowledge Base
          </p>
        </div>

        {/* Chat Messages Card */}
        <div style={{ 
          flex: 1, 
          marginBottom: '16px', 
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ 
            height: '55vh', 
            overflowY: 'auto',
            padding: '24px'
          }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '16px',
                  animation: 'fadeInUp 300ms ease-out forwards'
                }}
              >
                <div style={{
                  maxWidth: '85%',
                  padding: '12px 16px',
                  backgroundColor: msg.role === 'user' ? 'hsl(221 83% 53%)' : 'hsl(220 14% 96%)',
                  color: msg.role === 'user' ? 'white' : 'hsl(220 15% 15%)',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      {msg.role === 'user' ? '👤' : '🤖'}
                    </span>
                    <p style={{ 
                      margin: 0, 
                      whiteSpace: 'pre-wrap', 
                      lineHeight: '1.6',
                      fontSize: '14px'
                    }}>
                      {msg.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: '16px'
              }}>
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: 'hsl(220 14% 96%)',
                  borderRadius: '16px 16px 16px 4px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'hsl(220 9% 46%)' }}>
                    <span>🤖</span>
                    <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: '14px' }}>VISION is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div style={{ 
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: 'hsl(0 84% 95%)',
            border: '1px solid hsl(0 84% 80%)',
            borderRadius: '8px',
            color: 'hsl(0 84% 40%)'
          }}>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Input Area */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '16px'
        }}>
          <textarea
            ref={textareaRef}
            placeholder="Ask VISION anything..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={MAX_LENGTH}
            disabled={isLoading}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid hsl(220 13% 91%)',
              borderRadius: '8px',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit'
            }}
          />
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginTop: '12px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '12px',
              color: 'hsl(220 9% 46%)'
            }}>
              <Shield style={{ width: '14px', height: '14px' }} />
              <span>Protected</span>
              <span>•</span>
              <span>{input.length} / {MAX_LENGTH}</span>
            </div>
            <button 
              onClick={sendMessage}
              disabled={!input.trim() || input.length > MAX_LENGTH || isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: (!input.trim() || isLoading) ? 'hsl(220 14% 80%)' : 'hsl(221 83% 53%)',
                border: 'none',
                borderRadius: '8px',
                cursor: (!input.trim() || isLoading) ? 'not-allowed' : 'pointer',
                transition: 'background-color 200ms'
              }}
            >
              {isLoading ? (
                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <span>Send</span>
                  <Send style={{ width: '16px', height: '16px' }} />
                </>
              )}
            </button>
          </div>
        </div>

      </div>
      
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VisionChat;
